'use client' // Add this line at the top

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ChevronsRight, MoveUpRight, RouteOffIcon } from 'lucide-react'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import ReactCurvedText from 'react-curved-text'

export default function AboutThree(props: any) {
  const { icons, eyebrow, title, images, links, contactPageLink } = props
  const [index, setIndex] = useState(0)

  // Remove helper, render RichText directly for richText fields

  const handleClick = (i: number) => setIndex(i)
  return (
    <section className="about-three">
      <div className="container">
        <div className="flex flex-col lg:flex-row ">
          <div className="lg:w-[50%]">
            <div className="about-three__left md:ml-[60px] lg:ml-[100px] md:mr-[30px] ">
              <div className="about-three__img-box">
                <div className="about-three__img">
                  <Media
                    resource={images?.[0]?.image}
                    imgClassName="w-full h-auto rounded-3xl"
                    alt={images?.[0]?.alt || title}
                  />
                </div>
                <div className="about-three__img-2 hidden md:block">
                  <Media
                    resource={images?.[1]?.image}
                    imgClassName="w-full h-auto rounded-2xl"
                    alt={images?.[0]?.alt || title}
                  />
                </div>
                <div className="circle-text__rounded-text md:left-[-87px] left-[20%]  top-[30%] md:top-[64px]">
                  <div className="circle-text__curved-circle-box">
                    <Link href={contactPageLink?.url || '/contact'} className="curved-circle">
                      <div className="curved-text">
                        <svg viewBox="0 0 120 120">
                          <defs>
                            <path
                              id="circle"
                              d="M 60, 60 m -50, 0 a 50,50 0 1,1 100,0 a 50,50 0 1,1 -100,0"
                            />
                          </defs>
                          <text>
                            <textPath href="#circle" startOffset="0%">
                              CONTACT US • CONTACT US • CONTACT US •
                            </textPath>
                          </text>
                        </svg>
                      </div>
                    </Link>
                  </div>
                  <div className="circle-text__icon">
                    <a href="#">↗</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <section className="w-full lg:flex-1 space-y-8 mt-12 md:ml-[60px]  lg:ml-0">
            {/* Header */}
            <header className="text-left space-y-6">
              {eyebrow && (
                <RichText
                  className="border border-border px-4 text-xs py-1 rounded-full mb-2 w-fit flex items-center justify-center text-destructive"
                  data={eyebrow}
                  enableGutter={false}
                  enableProse={true}
                />
              )}
              {title && (
                <RichText
                  id="aboutblock-title"
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary bg-clip-text leading-tight"
                  data={title}
                  enableGutter={false}
                  enableProse={true}
                />
              )}
            </header>

            {/* Icons Grid */}
            {icons?.length > 0 && (
              <ul
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-4 md:gap-1 lg:gap-4 "
                role="list"
              >
                {icons.map((icon: any, i: number) => (
                  <li key={icon?.id || i}>
                    <button
                      type="button"
                      aria-pressed={index === i}
                      tabIndex={0}
                      className={`group border px-4 py-4 rounded-2xl  cursor-pointer transition-all duration-200  w-full md:w-[70%] lg:w-full hover:border-destructive ${index === i ? 'border-destructive' : 'border-border'}`}
                      onClick={() => handleClick(i)}
                    >
                      <div className="flex flex-row items-center text-center space-x-2">
                        {icon?.icon && (
                          <div className="flex-shrink-0">
                            <Media
                              resource={icon.icon}
                              className="w-10 h-10 object-contain"
                              alt={icon?.label || 'Icon'}
                            />
                          </div>
                        )}
                        {icon?.label && (
                          <h3 className="font-semibold text-primary leading-tight text-sm lg:text-base">
                            {icon.label}
                          </h3>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Dynamic Content */}
            {icons?.[index]?.content && (
              <RichText
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                data={icons[index].content}
                enableGutter={false}
                enableProse={true}
              />
            )}

            {/* Action Links */}
            {links?.length > 0 && (
              <nav aria-label="About actions" className="pt-4">
                <ul className="flex flex-wrap gap-4" role="list">
                  {links.map((link: any, i: number) => (
                    <li key={link?.id || i}>
                      <CMSLink
                        {...link.link}
                        className="relative overflow-hidden group  inline-flex items-center border border-border hover:border-black text-black cursor-pointer hover:text-white font-semibold text-base tracking-wide transition-colors duration-300 group/link rounded-full px-6 py-8 bg-transparent hover:bg-black focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <span className="w-12 h-12 rounded-full flex flex-1 items-center justify-center ml-2 bg-destructive text-white">
                          <ChevronsRight />
                        </span>
                        <span className="sr-only">{link?.label || 'Action'}</span>
                        <span
                          className="z-[-1] absolute inset-0 bg-black origin-center rotate-[-45deg] scale-y-0 group-hover:scale-y-[5] transition-transform duration-500 ease-in-out rounded-full"
                          aria-hidden="true"
                        />
                      </CMSLink>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </section>
        </div>
      </div>
    </section>
  )
}
